import React, { useEffect } from "react";

// Drain Sights — GoHighLevel "Web Quote Form" embed.
// The form is fully managed by GoHighLevel; we only render the iframe wrapper.
// The companion script (`form_embed.js`) is loaded once globally in
// `public/index.html` and auto-resizes the iframe based on form content.
const GHL_FORM_ID = "C7zX19ewNHNIqrCB9TNY";
const GHL_FORM_SRC = `https://link.reviewpresence.com/widget/form/${GHL_FORM_ID}`;
const GHL_FORM_NAME = "Web Quote Form - Drain Sights LLC";
// Initial height — the GHL script auto-resizes to the form's actual content
// height. Set to the form's declared height so the iframe shows fully before
// the script finishes booting / resizing.
const INITIAL_HEIGHT = 844;
// Delay before re-asking GHL's script to (re-)initialise the iframe, giving the
// async script time to finish booting after a route transition / mount.
const REINIT_DELAY_MS = 600;

export default function QuoteForm({ source = "quote" }) {
  // Each placement needs a unique DOM id so the GHL `form_embed.js` script
  // can address them independently.
  const uniqueId = `inline-${GHL_FORM_ID}-${source}`;

  // If the GHL script is already loaded, ask it to re-initialise any new
  // iframes that React just mounted. Without this, switching between pages
  // can leave the iframe un-resized.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const reinit = () => {
      try {
        if (window.LeadConnector && typeof window.LeadConnector.initIframe === "function") {
          window.LeadConnector.initIframe();
        }
      } catch (error) {
        // GHL script not ready yet — it picks the iframe up on its own load
        // (the script auto-scans for unresolved embeds). This is a recoverable
        // condition (the iframe self-resizes once the script boots), so we log
        // at debug level rather than surfacing an error to the user.
        if (process.env.NODE_ENV !== "production") {
          console.debug("QuoteForm: GHL initIframe not ready yet, will retry.", error);
        }
      }
    };
    reinit();
    const timer = setTimeout(reinit, REINIT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [uniqueId]);

  return (
    // Minimal wrapper: just a white card with a subtle shadow so the GHL form
    // stands out against the dark hero. Tightest possible padding so the iframe
    // sits nearly flush with the card edges — keeps the hero above the fold.
    <div
      id="quote"
      className="relative bg-white rounded-2xl shadow-[0_30px_60px_rgba(26,29,33,0.22)] border border-white/40 p-1"
      data-testid={`quote-form-${source}`}
      data-ghl-embed="true"
    >
      <div className="rounded-xl overflow-hidden" data-testid="quote-form-iframe-wrap">
        <iframe
          title={GHL_FORM_NAME}
          src={GHL_FORM_SRC}
          id={uniqueId}
          data-layout='{"id":"INLINE"}'
          data-trigger-type="alwaysShow"
          data-trigger-value=""
          data-activation-type="alwaysActivated"
          data-activation-value=""
          data-deactivation-type="neverDeactivate"
          data-deactivation-value=""
          data-form-name={GHL_FORM_NAME}
          data-height={INITIAL_HEIGHT}
          data-layout-iframe-id={uniqueId}
          data-form-id={GHL_FORM_ID}
          style={{ width: "100%", height: `${INITIAL_HEIGHT}px`, border: 0, borderRadius: "8px", display: "block" }}
        />
      </div>
    </div>
  );
}
